import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "../contexts/PermissionsContext";
import { Clock, Plane, DollarSign, Calculator } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import api from "../lib/api";
import TimeClockPage from "./TimeClockPage";
import PayrollPage from "./PayrollPage";
import TimeOffManager from "../components/TimeOffManager";

function RecomputeBreaksButton() {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const openDialog = async () => {
    setOpen(true); setPreview(null); setLoading(true);
    try {
      const res = await api.get("/timeclock/recompute-breaks/preview");
      setPreview(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load preview"); setOpen(false); }
    finally { setLoading(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const res = await api.post("/timeclock/recompute-breaks/apply");
      toast.success(`Updated ${res.data.entries_updated} shift${res.data.entries_updated !== 1 ? "s" : ""} · ${res.data.total_hours_removed}h of break time removed`);
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply"); }
    finally { setApplying(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={openDialog} data-testid="recompute-breaks-btn" className="rounded-full h-9 text-sm">
        <Calculator className="w-4 h-4 mr-1.5" strokeWidth={1.8} /> Recompute break deductions
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="recompute-breaks-dialog">
          <DialogHeader><DialogTitle>Recompute break deductions</DialogTitle></DialogHeader>
          {loading && <p className="text-sm text-slate-500 py-6 text-center">Scanning past shifts…</p>}
          {!loading && preview && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 font-['Manrope']">
                This re-derives each past clocked-out shift as <b>clock-out − clock-in − breaks</b> so historical hours and payroll are net of break time. It's safe to run repeatedly.
              </p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
                  <p className="text-2xl font-bold text-slate-900">{preview.entries_scanned}</p>
                  <p className="text-[11px] text-slate-500 uppercase tracking-wide">Scanned</p>
                </div>
                <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
                  <p className="text-2xl font-bold text-amber-700">{preview.entries_affected}</p>
                  <p className="text-[11px] text-amber-600 uppercase tracking-wide">To update</p>
                </div>
                <div className="rounded-xl bg-rose-50 border border-rose-100 p-3">
                  <p className="text-2xl font-bold text-rose-700">{preview.total_hours_removed}h</p>
                  <p className="text-[11px] text-rose-600 uppercase tracking-wide">Removed</p>
                </div>
              </div>
              {preview.entries_affected === 0 ? (
                <p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg p-3 text-center">All past shifts are already net of breaks. Nothing to change.</p>
              ) : (
                <div className="flex gap-2 justify-end pt-1">
                  <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full" data-testid="recompute-cancel-btn">Cancel</Button>
                  <Button onClick={apply} disabled={applying} className="rounded-full bg-slate-900 text-white hover:bg-slate-800" data-testid="recompute-apply-btn">
                    {applying ? "Applying…" : `Apply to ${preview.entries_affected} shift${preview.entries_affected !== 1 ? "s" : ""}`}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function TimesheetsPage() {
  const { user } = useAuth();
  const { canView } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const isManager = user?.role === "owner" || user?.role === "manager";

  const tabs = [
    { key: "timesheets", label: "Timesheets", icon: Clock, show: true },
    { key: "timeoff", label: "Time Off", icon: Plane, show: isManager },
    { key: "payroll", label: "Payroll", icon: DollarSign, show: canView("payroll") },
  ].filter(t => t.show);

  const requested = searchParams.get("tab") || "timesheets";
  const active = tabs.some(t => t.key === requested) ? requested : "timesheets";

  const setTab = (key) => {
    setSearchParams(key === "timesheets" ? {} : { tab: key }, { replace: true });
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="timesheets-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-slate-900">Timesheets</h1>
          <p className="text-slate-500 text-sm mt-1 font-['Manrope']">Hours, punches, breaks, time off, and payroll — all in one place</p>
        </div>
        {isManager && <RecomputeBreaksButton />}
      </div>

      <div className="flex items-center gap-1.5 mb-6 bg-white border border-slate-100 rounded-full p-1 w-fit" data-testid="timesheets-tabs">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`timesheets-tab-${t.key}`}
            className={`inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-semibold font-['Manrope'] transition-colors ${
              active === t.key ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <t.icon className="w-4 h-4" strokeWidth={1.8} />
            {t.label}
          </button>
        ))}
      </div>

      {active === "timesheets" && <TimeClockPage embedded />}
      {active === "timeoff" && (
        <div data-testid="timeoff-tab-content">
          <TimeOffManager />
        </div>
      )}
      {active === "payroll" && <PayrollPage embedded />}
    </div>
  );
}
